import React from "react";
import {
  Typography,
  Box,
  TextField,
  Grid,
  AccordionSummary,
  AccordionDetails,
  Accordion,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon
} from "@mui/icons-material";
import { OrganizationDTO } from "../../types/Tenant";

interface OrganizationDetailsPanelProps {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  organization: OrganizationDTO | null;
  orgName: string;
  orgAddress: string;
  orgCity: string;
  orgState: string;
  orgZipCode: string;
  orgPhone: string;
  orgWebsite: string;
  orgMission: string;
  orgVision: string;
  nameError: string;
  addressError: string;
  cityError: string;
  stateError: string;
  zipCodeError: string;
  phoneError: string;
  websiteError: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const OrganizationDetailsPanel: React.FC<OrganizationDetailsPanelProps> = ({
  expanded,
  onChange,
  organization,
  orgName,
  orgAddress,
  orgCity,
  orgState,
  orgZipCode,
  orgPhone,
  orgWebsite,
  orgMission,
  orgVision,
  nameError,
  addressError,
  cityError,
  stateError,
  zipCodeError,
  phoneError,
  websiteError,
  handleInputChange
}) => {
  // Format phone number to (123) 456-7890 format
  const formatPhoneNumber = (phoneNumber: string): string => {
    // Strip all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Check if the input is of correct length
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    
    // Return the original value if it doesn't match the pattern
    return phoneNumber;
  };
  
  // Handle phone field blur to format the phone number
  const handlePhoneBlur = () => {
    if (orgPhone) {
      // We can't directly update orgPhone since it's a prop
      // Instead, create a synthetic event to trigger the parent's handleInputChange
      const syntheticEvent = {
        target: {
          name: 'phone',
          value: formatPhoneNumber(orgPhone)
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      handleInputChange(syntheticEvent);
    }
  };
  return (
    <Accordion 
      expanded={expanded} 
      onChange={onChange}
      sx={{ mb: 2, borderRadius: '8px', overflow: 'hidden' }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="details-content"
        id="details-header"
        sx={{ backgroundColor: '#f5f5f5' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BusinessIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Details</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Organization Name"
              variant="outlined"
              fullWidth
              required
              value={orgName}
              onChange={handleInputChange}
              error={!!nameError}
              helperText={nameError}
              inputProps={{ maxLength: 200 }}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="street_address"
              label="Street Address"
              variant="outlined"
              fullWidth
              value={orgAddress}
              onChange={handleInputChange}
              error={!!addressError}
              helperText={addressError}
              inputProps={{ maxLength: 200 }}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="city"
                label="City"
                variant="outlined"
                fullWidth
                value={orgCity}
                onChange={handleInputChange}
                error={!!cityError}
                helperText={cityError}
                inputProps={{ maxLength: 100 }}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                name="state"
                label="State"
                variant="outlined"
                fullWidth
                value={orgState}
                onChange={handleInputChange}
                error={!!stateError}
                helperText={stateError}
                inputProps={{ maxLength: 2 }}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={8} sm={4}>
              <TextField
                name="zip_code"
                label="Zip Code"
                variant="outlined"
                fullWidth
                value={orgZipCode}
                onChange={handleInputChange}
                error={!!zipCodeError}
                helperText={zipCodeError}
                inputProps={{ maxLength: 5 }}
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="phone"
              label="Phone"
              variant="outlined"
              fullWidth
              value={orgPhone}
              onChange={handleInputChange}
              onBlur={handlePhoneBlur}
              error={!!phoneError}
              helperText={phoneError}
              inputProps={{ maxLength: 14 }}
              placeholder="(123) 456-7890"
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="website"
              label="Website"
              variant="outlined"
              fullWidth
              value={orgWebsite}
              onChange={handleInputChange}
              error={!!websiteError}
              helperText={websiteError}
              inputProps={{ maxLength: 200 }}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="mission"
              label="Mission"
              variant="outlined"
              multiline
              minRows={3}
              fullWidth
              value={orgMission}
              onChange={handleInputChange}
              sx={{ 
                mb: 2,
                '& .MuiInputBase-root': {
                  resize: 'vertical',
                  overflow: 'auto',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '3px',
                    right: '3px',
                    width: '10px',
                    height: '10px',
                    background: 'linear-gradient(135deg, transparent 50%, #757575 50%)',
                    cursor: 'se-resize',
                    pointerEvents: 'none',
                    borderRadius: '1px',
                  }
                },
                '& textarea': {
                  resize: 'none',
                  textAlign: 'left',
                  verticalAlign: 'top',
                  padding: '10px 14px !important',
                  lineHeight: '1.5',
                }
              }}
              InputProps={{
                style: { minHeight: '100px', maxHeight: '300px' },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="vision"
              label="Vision"
              variant="outlined"
              multiline
              minRows={3}
              fullWidth
              value={orgVision}
              onChange={handleInputChange}
              sx={{ 
                mb: 2,
                '& .MuiInputBase-root': {
                  resize: 'vertical',
                  overflow: 'auto',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '3px',
                    right: '3px',
                    width: '10px',
                    height: '10px',
                    background: 'linear-gradient(135deg, transparent 50%, #757575 50%)',
                    cursor: 'se-resize',
                    pointerEvents: 'none',
                    borderRadius: '1px',
                  }
                },
                '& textarea': {
                  resize: 'none',
                  textAlign: 'left',
                  verticalAlign: 'top',
                  padding: '10px 14px !important',
                  lineHeight: '1.5',
                }
              }}
              InputProps={{
                style: { minHeight: '100px', maxHeight: '300px' },
              }}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default OrganizationDetailsPanel;
